import { toLatinDigits, toPersianDigits } from '@clinic/money-date';

/** Characters that carry meaning in a toman amount; everything else is formatting. */
const SIGNIFICANT = /[0-9.-]/;

function normalize(text: string): string {
  return toLatinDigits(text).replace(/[٫]/g, '.').replace(/[−–]/g, '-');
}

/**
 * Formats a toman amount while the user types it (pilot feedback #1): digits are grouped by three
 * with commas and shown in Persian, a minus is kept only at the start (when allowed), and at most
 * one decimal digit follows a point (1 toman = 10 rial). The caret stays after the same digit it
 * was after. Reading the value is still `parseTomanInput`, which ignores the commas.
 */
export function formatMoneyTyping(
  raw: string,
  caret: number,
  allowNegative = false,
): { text: string; caret: number } {
  const text = normalize(raw);
  // How many meaningful characters were before the caret: the caret goes back after as many.
  const before = [...text.slice(0, caret)].filter((c) => SIGNIFICANT.test(c)).length;

  const negative = allowNegative && text.trimStart().startsWith('-');
  const body = text.replace(/[^0-9.]/g, '');
  const point = body.indexOf('.');
  let whole = point === -1 ? body : body.slice(0, point);
  const fraction = point === -1 ? null : body.slice(point + 1).replace(/\./g, '').slice(0, 1);
  whole = whole.replace(/^0+(?=\d)/, '');

  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const out = `${negative ? '-' : ''}${grouped}${fraction === null ? '' : `.${fraction}`}`;

  let seen = 0;
  let position = out.length;
  if (before === 0) position = 0;
  else {
    for (let i = 0; i < out.length; i += 1) {
      if (SIGNIFICANT.test(out[i] as string)) seen += 1;
      if (seen === before) {
        position = i + 1;
        break;
      }
    }
  }
  return { text: toPersianDigits(out), caret: position };
}
